import io from 'socket.io-client';
import { store } from '../store/store';

import { setBoard, setFilterBoard } from '../store/board/reducers/board.reducer';
import { addGroup, updateGroup, removeGroup } from '../store/group/reducers/group.reducer';
import { addGroupTask, updateGroupTask, removeGroupTask, setCurrentSubtask, updateTask, removeTasksFromStore } from '../store/task/reducers/task.reducer';

import { setInviteUsers, setMembersAccount } from '../store/accounts/reducers/account.reducer';
import { setDesktops, setSelectedDesktop } from '../store/desktop/reducers/desktop.reducer';
import { setDashboard, updateDashboard } from '../store/dashboard/reducers/dashboard.reducer';
import { setCurrentUser } from '../store/user/reducers/auth.reducer';
import { setPlan } from '../store/plans/reducers/plan.reducer';
import { addMemberGroup, updateMemberGroup, removeMemberGroup } from '../store/memberGroups/reducers/memberGroup.reducer';
import { doLogout, updateUserType } from '../store/user/actions/user.actions';
import { fetchAccount } from '../store/accounts/actions/account.actions';

const getAccountIdFromStore = () => {
    try {
        const s = store.getState();
        return s?.accounts?.selectedAccount?.accountId?._id
            || null;
    } catch { return null; }
};

function pokeConnection() {
    const id = getAccountIdFromStore();
    if (!id) return;

    // só conecta se estiver realmente off
    if (!socket.connected && !socket.connecting) socket.connect();

    // se já tiver conectado, reforça o join (id sempre atual da store)
    if (socket.connected) socket.emit("joinAccount", id);
}

const { protocol, hostname, port } = window.location;

const BACKEND = process.env.REACT_APP_BACKEND_HOST; // ex: https://api.seusite.com
// Fallback seguro: mesmo host da página
const SAME_ORIGIN = `${protocol}//${hostname}${port ? `:${port}` : ""}`;

function normalizeSocketUrl(url) {
    if (!url) return SAME_ORIGIN;
    try {
        const u = new URL(url);
        if (protocol === "https:" && u.protocol === "http:") u.protocol = "https:";
        return u.toString();
    } catch {
        return SAME_ORIGIN;
    }
}

const SOCKET_URL = normalizeSocketUrl(BACKEND);

const socket = io.connect(SOCKET_URL, {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity, // antes estava 60 tentativas, agora sempre vai ficar tentando reconectar
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000, // mais tempo para tentar reconectar 
    withCredentials: true,
});

let reconnectListenerSet = false;

export const connectSocket = (accountId) => {
    if (!accountId) {
        console.warn("connectSocket chamado sem um accountId válido.");
        return;
    }

    if (socket.connected) {
        socket.emit("joinAccount", accountId);
        //console.log("Já conectado ao socket.");
    }

    socket.once("connect", () => {
        socket.emit("joinAccount", accountId);
        //console.log(`Entrou na conta (connect): ${accountId}`);
    });

    if (!socket.active) {
        socket.connect();
    }
};


export const disconnectSocket = (accountId) => {
    if (!socket.connected) return console.log("Socket já desconectado.");
    socket.emit("leaveAccount", accountId);
    socket.disconnect();
    console.log(`Saiu da conta: ${accountId}`);
};

const updateState = (setter, newValue) => store.dispatch(setter(newValue));
const updateListItem = (list, updatedItem) => list.map(item => item._id === updatedItem._id ? updatedItem : item);
const removeListItem = (list, id) => list.filter(item => item._id !== id);

const handleBoardEvent = (board) => {
    pokeConnection();
    const { board: prevBoard = {}, filteredBoard = {}, filterBy } = store.getState().board;

    if (prevBoard?._id === board._id || filteredBoard?._id === board._id) {
        updateState(setBoard, { board });
        updateState(setFilterBoard, { filteredBoard: board });
    }
};

const handleAddBoardEvent = ({ newBoard, updatedDesktop }) => {
    pokeConnection();
    const state = store.getState();
    updateState(setDesktops, updateListItem(state.desktops.desktops, updatedDesktop));
    if (state.desktops.selectedDesktop?._id === updatedDesktop._id) {
        updateState(setSelectedDesktop, { selectedDesktop: updatedDesktop });
    }
    handleBoardEvent(newBoard);
};

const handleUpdateCmpsOrder = ({ boardId, cmpsOrder } = {}) => {
    pokeConnection();

    if (!boardId || !Array.isArray(cmpsOrder)) return;

    const state = store.getState();
    const currentBoard = state.board?.board;
    const currentFiltered = state.board?.filteredBoard;

    const shouldUpdateBoard = currentBoard?._id && String(currentBoard._id) === String(boardId);
    const shouldUpdateFiltered = currentFiltered?._id && String(currentFiltered._id) === String(boardId);

    // Se não é a board que o usuário está vendo, não faz nada
    if (!shouldUpdateBoard && !shouldUpdateFiltered) return;

    if (shouldUpdateBoard) {
        const mergedBoard = { ...currentBoard, cmpsOrder };
        updateState(setBoard, { board: mergedBoard });
    }

    if (shouldUpdateFiltered) {
        const mergedFiltered = { ...currentFiltered, cmpsOrder };
        updateState(setFilterBoard, { filteredBoard: mergedFiltered });
    }
};


const handleAddGroup = (group) => {
    pokeConnection();
    const state = store.getState();
    const exists = state.groups.groups.some(g => g._id === group._id);

    if (exists) {
        //console.log(`Grupo ${group._id} já existe no Redux. Ignorando.`);
        return;
    }

    updateState(addGroup, { group });
};

const handleUpdateGroup = ({ group }) => {
    pokeConnection();
    updateState(updateGroup, { group });
};

const handleDeleteGroup = (groupId) => {
    pokeConnection();
    updateState(removeGroup, { groupId });
};

const handleGroupsUpdated = ({ groupsChanged, tasksChanged }) => {
    try {
        pokeConnection?.();

        const groups = Array.isArray(groupsChanged)
            ? groupsChanged
            : [];
        const tasks = Array.isArray(tasksChanged) ? tasksChanged : [];


        if (!groups.length && !tasks.length) return;

        for (const g of groups) {
            if (!g?._id) continue;
            updateState(updateGroup, { group: g });
        }

        for (const t of tasks) {
            if (!t?._id) continue;
            handleUpdateTask(t);
        }
    } catch (err) {
        console.error("handleGroupsUpdated error:", err, groupsChanged);
    }
};

const handleAddTask = ({ task, group }) => {
    console.log('task', task);
    console.log('group', group);
    pokeConnection();

    const state = store.getState();
    const gid = group?._id;
    const taskId = task?._id;

    const alreadyExists =
        Boolean(gid && taskId) &&
        Array.isArray(state.tasks?.tasksByGroupId?.[gid]) &&
        state.tasks.tasksByGroupId[gid].includes(taskId);

    if (!alreadyExists) {
        updateState(addGroupTask, { groupId: gid, task });
    }

    if (group) {
        updateState(updateGroup, { group });
    }
};

export const handleUpdateTask = (payload) => {
    pokeConnection();
    const state = store.getState();

    if (payload?.taskId && payload?.patch) {
        const existing = state.tasks?.taskById?.[payload.taskId];
        if (existing) {
            const merged = { ...existing, ...payload.patch };
            updateState(updateTask, { task: merged });
        }
        return;
    }

    const task = payload?.task ?? payload;

    if (task?._id) {
        updateState(updateTask, { task });
    } else {
        return;
    }

    if (task?.parentId) {
        updateState(setCurrentSubtask, { currentSubtask: task });
        return;
    }

    //  localizar o groupId no tasksByGroupId
    const byGroup = state.tasks?.tasksByGroupId || {};
    const taskId = task._id;

    for (const gid of Object.keys(byGroup)) {
        const arr = byGroup[gid] || [];

        const found =
            arr.includes?.(taskId) ||
            arr.some?.(t => t?._id === taskId);

        if (found) {
            updateState(updateGroupTask, { groupId: gid, task });
            break;
        }
    }
};

export const handleRemoveTask = ({ groupId, tasksId }) => {
    pokeConnection();
    if (Array.isArray(tasksId) && tasksId.length) {
        updateState(removeTasksFromStore, { groupId, taskIds: tasksId });
    } else {
        updateState(removeTasksFromStore, { groupId, tasksId });
    }
};

const handleDeleteTask = (taskId, groupId) => {
    pokeConnection();
    updateState(removeGroupTask, { groupId, taskId });

    const state = store.getState();
    const group = state.groups.groups.find(g => g._id === groupId);
    if (group) {
        const updatedGroup = {
            ...group,
            tasks: group.tasks.filter(id => id !== taskId),
        };
        updateState(updateGroup, { group: updatedGroup });
    }
};

const handleAddDesktop = ({ newDesktop }) => {
    pokeConnection();
    if (!newDesktop?._id) return console.warn("Dados inválidos recebidos.");
    const state = store.getState();
    if (state.desktops.desktops.some(d => d._id === newDesktop._id)) return;
    updateState(setDesktops, [...state.desktops.desktops, newDesktop]);
};

const handleUpdateDesktop = (updatedDesktop) => {
    pokeConnection();
    if (!updatedDesktop?._id) return console.warn("Dados inválidos recebidos.");
    const state = store.getState();
    updateState(setDesktops, updateListItem(state.desktops.desktops, updatedDesktop));
    if (state.desktops.selectedDesktop?._id === updatedDesktop._id) {
        updateState(setSelectedDesktop, { selectedDesktop: updatedDesktop });
    }
};

const handleDeleteDesktop = (deletedDesktopId) => {
    pokeConnection();
    if (!deletedDesktopId) return console.warn("ID inválido recebido.");
    const state = store.getState();
    updateState(setDesktops, removeListItem(state.desktops.desktops, deletedDesktopId));
    if (state.desktops.selectedDesktop?._id === deletedDesktopId) {
        updateState(setSelectedDesktop, { selectedDesktop: null });
    }
};

const handleSyncTitle = ({ desktop, board, isFolder }) => {
    pokeConnection();
    const state = store.getState();
    updateState(setDesktops, updateListItem(state.desktops.desktops, desktop));

    // só atualize o selectedDesktop se o usuário já estiver nesse desktop
    if (state.desktops.selectedDesktop?._id === desktop._id) {
        updateState(setSelectedDesktop, { selectedDesktop: desktop });
    }

    // só toca a board se:
    //    não for pasta e
    //    o board recebido for exatamente o que o usuário está vendo (board OU filteredBoard)
    if (!isFolder) {
        const currentBoardId = state.board.board?._id;
        const currentFilteredId = state.board.filteredBoard?._id;
        if (board?._id && (board._id === currentBoardId || board._id === currentFilteredId)) {
            updateState(setBoard, { board });
            updateState(setFilterBoard, { filteredBoard: board });
        }
    }
};

const handleUpdatePlan = (plan) => {
    pokeConnection();
    if (plan) {
        updateState(setPlan, plan);
    }
};

const handleUpdateAccount = async (account) => {
    pokeConnection();
    if (account) {
        const accountId = account?._id || account;
        await fetchAccount(accountId);
    }
};

const handleUpdateMember = async (userId, accountId) => {
    pokeConnection();
    const state = store.getState();
    const currentUser = state.auth.currentUser?._id;
    const sameUser = userId === currentUser;
    if (accountId) {
        const account = await fetchAccount(accountId);
        const userInAccount = account?.users?.find(user => user.userId?._id === userId);
        if (!userInAccount) {
            console.error("Usuário não encontrado nesta conta");
            return;
        }

        if (sameUser) {
            if (userInAccount?.active === false) {
                doLogout();
                return;
            }
            await updateUserType(userInAccount.typeUser);
        }
    }
};

const handleUpdateMembers = (data) => {
    pokeConnection();
    if (data.updatedAccount.users) {
        updateState(setMembersAccount, data.updatedAccount.users);
        updateState(setInviteUsers, data.updatedInviteUsers);
    }
};

const handleUpdateInvites = (data) => {
    pokeConnection();
    if (data.inviteUsers) {
        updateState(setMembersAccount, data.users);
        updateState(setInviteUsers, data.inviteUsers);
    }
}

const handleAddMembers = (data) => {
    pokeConnection();
    if (data.users) {
        updateState(setMembersAccount, data.users);
    }
}

const handleUpdateDashboard = (updatedDashboard) => {
    pokeConnection();

    if (!updatedDashboard?._id) return;

    const currentDashboard = store.getState().dashboard.dashboard;
    if (!currentDashboard?._id) return;
    if (String(currentDashboard._id) !== String(updatedDashboard._id)) return;

    const nextDashboard = {
        ...currentDashboard,
        ...updatedDashboard,
        dynamicData:
            Array.isArray(updatedDashboard?.dynamicData) && updatedDashboard.dynamicData.length
                ? updatedDashboard.dynamicData
                : currentDashboard.dynamicData,
        orderData:
            Array.isArray(updatedDashboard?.orderData) && updatedDashboard.orderData.length
                ? updatedDashboard.orderData
                : currentDashboard.orderData,
    };

    updateState(updateDashboard, { dashboard: nextDashboard });
};

const handleTaskMoved = ({ task, fromGroup, toGroup }) => {
    pokeConnection();

    updateState(updateGroup, { group: fromGroup });
    updateState(updateGroup, { group: toGroup });

    // move no cache se existir (não quebra se não existir)
    const state = store.getState();
    const byGroup = state.tasks.tasksByGroupId || {};

    const fromArr = byGroup[fromGroup?._id] || [];
    const toArr = byGroup[toGroup?._id] || [];

    const inFrom = fromArr.includes(task?._id);
    const inTo = toArr.includes(task?._id);

    if (inFrom) updateState(removeGroupTask, { groupId: fromGroup._id, taskId: task._id });
    if (!inTo) updateState(addGroupTask, { groupId: toGroup._id, task });

    // e atualiza os dados da task onde ela estiver
    handleUpdateTask(task);
};

const handleMemberGroupCreated = ({ group }) => {
    pokeConnection();
    if (group) updateState(addMemberGroup, group);
};

const handleMemberGroupUpdated = ({ group }) => {
    pokeConnection();
    if (group) updateState(updateMemberGroup, group);
};

const handleMemberGroupArchived = ({ groupId }) => {
    pokeConnection();
    if (groupId) updateState(removeMemberGroup, groupId);
};

const handleInactivePlan = async () => {
    const state = store.getState();
    const currentUser = state.auth.currentUser;
    const selectedAccount = state.accounts?.selectedAccount?.accountId;


    if (!currentUser || !selectedAccount) return;

    const responsibleId = selectedAccount?.responsibleId;

    // Se não for o responsável, desloga
    if (String(currentUser?._id) !== String(responsibleId)) {
        doLogout(currentUser?._id);
        return;
    }

    await fetchAccount(selectedAccount?._id);
};

const socketEvents = {
    board: [
        { event: 'addBoard', handler: handleAddBoardEvent },
        { event: 'updateBoards', handler: handleBoardEvent },
        { event: 'deleteBoard', handler: handleBoardEvent },
        { event: 'updateCmpsOrder', handler: handleUpdateCmpsOrder },
    ],
    dashboard: [
        { event: 'updateDashboard', handler: handleUpdateDashboard }
    ],
    group: [
        { event: 'addGroup', handler: handleAddGroup },
        { event: 'updateGroup', handler: handleUpdateGroup },
        { event: 'deleteGroup', handler: handleDeleteGroup },
        { event: 'groupsUpdated', handler: handleGroupsUpdated },
    ],
    task: [
        { event: 'addTask', handler: handleAddTask },
        { event: 'updateTask', handler: handleUpdateTask },
        { event: 'removeTasks', handler: handleRemoveTask },
        { event: 'deleteTask', handler: handleDeleteTask },
        { event: 'taskMoved', handler: handleTaskMoved },
    ],
    desktop: [
        { event: 'addDesktop', handler: handleAddDesktop },
        { event: 'updateDesktop', handler: handleUpdateDesktop },
        { event: 'removeDesktop', handler: handleDeleteDesktop },
    ],
    tree: [
        { event: 'syncTitle', handler: handleSyncTitle },
    ],
    members: [
        { event: 'AddMembersAccount', handler: handleAddMembers },
    ],
    invites: [
        { event: 'updateMembersAccount', handler: handleUpdateMembers },
        { event: 'updateInvitesAccount', handler: handleUpdateInvites },
    ],
    auth: [
        { event: 'inactivePlan', handler: handleInactivePlan },
    ],
    plan: [
        { event: 'updatePlan', handler: handleUpdatePlan },
    ],
    account: [
        { event: 'updateAccount', handler: handleUpdateAccount },
        { event: 'updateMember', handler: handleUpdateMember },
    ],
    memberGroups: [
        { event: 'memberGroupCreated', handler: handleMemberGroupCreated },
        { event: 'memberGroupUpdated', handler: handleMemberGroupUpdated },
        { event: 'memberGroupArchived', handler: handleMemberGroupArchived },
    ],
};

let listenersInitialized = false;

export const initializeSocketListeners = (initialAccountId) => {
    if (listenersInitialized) return;
    socket.on('connect_error', err => console.error('Erro de conexão:', err));

    Object.values(socketEvents).flat().forEach(({ event, handler }) => {
        socket.on(event, handler);
    });


    // Sempre que conectar/reconectar, pega o accountId atual do Redux e reentra na sala
    const joinCurrentAccountRoom = () => {
        const state = store.getState();
        const acc =
            state.accounts?.selectedAccount?.accountId ||
            initialAccountId;
        if (acc) socket.emit("joinAccount", acc._id || acc);

    };

    socket.on("connect", joinCurrentAccountRoom);
    socket.on("reconnect", joinCurrentAccountRoom);
    
    joinCurrentAccountRoom();
    if (socket.connected) joinCurrentAccountRoom();

    socket.on("connect", joinCurrentAccountRoom);

    socket.on("reconnect", () => {
        // garante nova entrada na room após reconexão
        joinCurrentAccountRoom();
    });

    socket.on("reconnect_attempt", () => {
        // se precisar, força .connect() (alguns ambientes congelam a aba)
        if (!socket.active) socket.connect();
    });

    // Quando a aba volta ao foco/visível, garante que está na room certa
    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
            if (!document.hidden) {
                if (!socket.connected) socket.connect();
                joinCurrentAccountRoom();
            }
        });
        window.addEventListener("focus", () => {
            if (!socket.connected) socket.connect();
            joinCurrentAccountRoom();
        });
    }
    listenersInitialized = true;
};


export { socket };
